"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import LoadingLink from "@/components/navigation/LoadingLink";
import { supabase } from "@/lib/supabaseClient";
import { ASSESSMENT_SEGMENTS } from "@/lib/assessmentSegments";
import {
  profileCompletionRedirectUrl,
  shouldBlockAssessmentAccess,
  PROFILE_GUARD_SELECT,
} from "@/lib/profileCompleteness";

type SegmentStatus = "locked" | "available" | "in_progress" | "complete";

type AssessmentRow = {
  layer: number | null;
  current_index: number | null;
  total_questions: number | null;
  completed_at: string | null;
};

export default function AssessmentSegmentSelector() {
  const router = useRouter();
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);
  const [isPartnerStudent, setIsPartnerStudent] = useState(false);
  const [segmentData, setSegmentData] = useState<
    Record<number, AssessmentRow | null>
  >({});

  useEffect(() => {
    const load = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        router.replace("/login?redirect=/assessment");
        return;
      }
      setIsPartnerStudent(Boolean(user.user_metadata?.partner_b2b_student));

      const [{ data: profile }, { data: anyAssessment }] = await Promise.all([
        supabase
          .from("profiles")
          .select(PROFILE_GUARD_SELECT)
          .eq("id", user.id)
          .maybeSingle(),
        supabase
          .from("assessments")
          .select("id")
          .eq("user_id", user.id)
          .limit(1)
          .maybeSingle(),
      ]);

      if (
        shouldBlockAssessmentAccess(profile, Boolean(anyAssessment))
      ) {
        router.replace(
          profileCompletionRedirectUrl(
            "/assessment",
            user.user_metadata?.partner_b2b_student
              ? "/partner/student/profile"
              : undefined,
          ),
        );
        return;
      }

      setUserId(user.id);

      const { data: rows } = await supabase
        .from("assessments")
        .select("layer, current_index, total_questions, completed_at")
        .eq("user_id", user.id)
        .in("layer", [1, 2, 3, 4, 5]);

      const byLayer: Record<number, AssessmentRow | null> = {};
      for (const seg of ASSESSMENT_SEGMENTS) {
        const row = (rows ?? []).find((r) => r.layer === seg.layer);
        byLayer[seg.layer] = row
          ? {
              layer: row.layer,
              current_index: row.current_index,
              total_questions: row.total_questions,
              completed_at: row.completed_at,
            }
          : null;
      }
      setSegmentData(byLayer);
      setLoading(false);
    };
    void load();
  }, [router]);

  if (loading || !userId) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center px-4 text-slate-100">
        <p className="text-sm text-slate-400">Loading assessment...</p>
      </div>
    );
  }

  function getStatus(segmentIndex: number): SegmentStatus {
    const seg = ASSESSMENT_SEGMENTS[segmentIndex];
    const row = segmentData[seg.layer] ?? null;
    const completed = Boolean(
      row?.completed_at ||
        (row?.current_index != null &&
          row?.total_questions != null &&
          row.current_index >= row.total_questions),
    );
    if (completed) return "complete";
    const inProgress =
      row &&
      row.current_index != null &&
      row.current_index > 0 &&
      !completed;
    if (inProgress) return "in_progress";
    const prevComplete =
      segmentIndex === 0 ||
      (() => {
        const prev = ASSESSMENT_SEGMENTS[segmentIndex - 1];
        const prevRow = segmentData[prev.layer] ?? null;
        return Boolean(
          prevRow?.completed_at ||
            (prevRow?.current_index != null &&
              prevRow?.total_questions != null &&
              prevRow.current_index >= prevRow.total_questions),
        );
      })();
    return prevComplete ? "available" : "locked";
  }

  const allComplete = ASSESSMENT_SEGMENTS.every((seg) => {
    const row = segmentData[seg.layer] ?? null;
    return (
      row?.completed_at ||
      (row?.current_index != null &&
        row?.total_questions != null &&
        row.current_index >= row.total_questions)
    );
  });

  return (
    <div className="min-h-screen px-4 pb-16 pt-8 text-slate-100 sm:px-6 sm:pt-12">
      <div className="mx-auto max-w-2xl space-y-8">
        <header className="space-y-2">
          <h1 className="font-heading text-2xl font-semibold text-slate-50 sm:text-3xl">
            Assessment segments
          </h1>
          <p className="text-sm text-slate-400">
            Questions are personalized from your profile. Complete each segment in order —
            the next unlocks only after you finish the previous. Each segment can be done
            in a separate session. Completed segments are view-only (no retake).
          </p>
        </header>

        <div className="space-y-4">
          {ASSESSMENT_SEGMENTS.map((seg, index) => {
            const status = getStatus(index);
            const row = segmentData[seg.layer] ?? null;
            const progress =
              row?.total_questions && row?.current_index != null
                ? Math.min(
                    Math.round((row.current_index / row.total_questions) * 100),
                    100,
                  )
                : 0;
            const isLocked = status === "locked";
            const href = `/assessment/${seg.id}`;

            const card = (
              <div
                className={`rounded-2xl border p-4 transition sm:p-5 ${
                  isLocked
                    ? "cursor-not-allowed border-slate-800 bg-slate-900/30 opacity-75"
                    : "border-slate-800 bg-slate-900/80 ring-1 ring-slate-800 hover:ring-[var(--partner-secondary)]/50"
                }`}
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--partner-secondary)]/20 text-sm font-bold text-[var(--partner-secondary)]">
                      {index + 1}
                    </span>
                    <div>
                      <p className="font-semibold text-slate-50">
                        {seg.title}
                      </p>
                      <p className="text-xs text-slate-400">
                        {status === "complete"
                          ? "Completed — view your answers (no retake)"
                          : `${seg.description} · ~${seg.estimatedMinutes} min`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {status === "complete" && (
                      <span className="rounded-full bg-emerald-500/20 px-2.5 py-0.5 text-xs font-semibold text-emerald-300">
                        View answers
                      </span>
                    )}
                    {status === "in_progress" && (
                      <span className="rounded-full bg-[var(--partner-primary)]/20 px-2.5 py-0.5 text-xs font-semibold text-[var(--partner-primary)]">
                        {progress}%
                      </span>
                    )}
                    {status === "available" && (
                      <span className="rounded-full bg-[var(--partner-secondary)]/20 px-2.5 py-0.5 text-xs font-semibold text-[var(--partner-secondary)]">
                        Start
                      </span>
                    )}
                    {status === "locked" && (
                      <span className="rounded-full bg-slate-600/30 px-2.5 py-0.5 text-xs font-medium text-slate-500">
                        Complete previous to unlock
                      </span>
                    )}
                  </div>
                </div>
                {status === "in_progress" && (
                  <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-slate-800">
                    <div
                      className="h-full rounded-full bg-[var(--partner-primary)] transition-all"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                )}
              </div>
            );

            if (isLocked) {
              return <div key={seg.id}>{card}</div>;
            }
            return (
              <LoadingLink key={seg.id} href={href}>
                {card}
              </LoadingLink>
            );
          })}
        </div>

        {allComplete && userId && (
          <div className="rounded-2xl border border-emerald-800/60 bg-emerald-950/30 p-4 text-center">
            <p className="text-sm font-medium text-emerald-200">
              All segments complete
            </p>
            <p className="mt-1 text-xs text-slate-400">
              Your career report is ready. View your personalised insights.
            </p>
            <LoadingLink
              href={`/report/${userId}`}
              className="mt-4 inline-flex rounded-full bg-[var(--partner-primary)] px-5 py-2.5 text-sm font-semibold text-slate-950 shadow-md transition hover:opacity-90"
            >
              View my career report
            </LoadingLink>
          </div>
        )}

        <p className="text-center text-xs text-slate-500">
          <LoadingLink
            href={isPartnerStudent ? "/partner/student/dashboard" : "/dashboard"}
            className="underline hover:text-slate-300"
          >
            Back to dashboard
          </LoadingLink>
        </p>
      </div>
    </div>
  );
}
